import { formatDurationForHumans } from 'components/common/utils';
import { ContentClassEnum } from 'content-service-client';
import {
    AudioSearchResponse,
    GetSimilarAudioItems200Response,
    SearchUrlParamKeys,
    getArrayValueFromSearchParam,
    removeArrayValueFromSearchParam,
} from 'search-gateway-client';
import { SearchContentClass } from 'stockblocks-client/src/StockblocksClient';

import { SearchParams } from '../../../../../../shared/types';
import { extraAppliedFilters, filterConfig } from './FilterConfig';
import { AppliedFilter, FilterConfig, FilterOption } from './FilterTypes';

export const DEFAULT_MIN_DURATION = 0;
export const DEFAULT_MAX_DURATION = 240;

export const mapSearchParamsToAudioExtraAppliedFilters = (
    searchParams: SearchParams,
    searchData: AudioSearchResponse | GetSimilarAudioItems200Response,
) => {
    const finalFilters: AppliedFilter[] = [];
    const extraFilterConfig = extraAppliedFilters['audio'];

    for (const filter of extraFilterConfig) {
        const { key } = filter;
        const searchParamValue = searchParams[key];
        if (searchParamValue === undefined) continue;

        if (searchParamValue) {
            // Search params of the same key are condensed into a single string array, so the value could be string | string[].
            // If it's a string, it might also be a comma-separated string array.
            const values: string[] = Array.isArray(searchParamValue)
                ? searchParamValue
                : getArrayValueFromSearchParam(searchParamValue);

            for (const value of values) {
                const filterValue = getAudioStockItemValueFromSearchParam(key, value, searchData);

                if (filterValue) {
                    finalFilters.push({
                        key,
                        value,
                        valueLabel: filter.getValueLabel(filterValue),
                    });
                }
            }
        }
    }

    return finalFilters;
};

export const getAudioStockItemValueFromSearchParam = (
    searchParamKey: string,
    searchParamValue: string,
    searchData: AudioSearchResponse | GetSimilarAudioItems200Response,
) => {
    switch (searchParamKey) {
        case SearchUrlParamKeys.SEARCH_SIMILAR_ID:
            return searchParamValue && ((searchData as GetSimilarAudioItems200Response)?.similarToItem?.title || '');
        case SearchUrlParamKeys.ARTIST_IDS:
            const seenArtistIds = new Set(getArrayValueFromSearchParam(searchParamValue));
            let artistNames = '';
            const typedSearchData = searchData as AudioSearchResponse;
            if (!typedSearchData?.stockItems) return '';

            for (const artistId of seenArtistIds) {
                const artist = typedSearchData.stockItems
                    .flatMap((item) => item.artists)
                    .find((artist) => artist.id === artistId);

                if (artist) {
                    const artistName = (artist.name || artist.alias || '').trim();
                    if (artistName) {
                        artistNames += `${!artistNames ? '' : '_'}${artistName}`;
                    }
                }
            }
            return artistNames;
        default:
            return '';
    }
};

export const removeFilterArrayValueFromSearchParam = (
    paramValue: string,
    filter: AppliedFilter,
    delimiter: string = '_',
) => {
    return removeArrayValueFromSearchParam(paramValue, filter?.urlId ?? filter.value, delimiter);
};

export function getDurationLabel(
    min: number,
    max: number,
    minDefault = DEFAULT_MIN_DURATION,
    maxDefault = DEFAULT_MAX_DURATION,
) {
    if (min !== minDefault && max !== maxDefault) {
        return `Duration: ${formatDurationForHumans(min, { maxNumberOfSeconds: DEFAULT_MAX_DURATION })} - ${formatDurationForHumans(max, { maxNumberOfSeconds: DEFAULT_MAX_DURATION })}`;
    }
    if (min !== minDefault) {
        const formattedMin = formatDurationForHumans(min, { maxNumberOfSeconds: DEFAULT_MAX_DURATION });
        // Avoid double '+' signs if formatDurationForHumans already added one
        const suffix = formattedMin.endsWith('+') ? '' : '+';
        return `Duration: ${formattedMin}${suffix}`;
    }
    if (max !== maxDefault) {
        return `Duration: up to ${formatDurationForHumans(max, { maxNumberOfSeconds: DEFAULT_MAX_DURATION })}`;
    }
    return '';
}

export function getAppliedFilters(activeFilters: FilterConfig[], searchParams: SearchParams): AppliedFilter[] {
    const applied: AppliedFilter[] = [];

    for (const filter of activeFilters) {
        const { key, enableCondition, type } = filter;
        const urlSearchParams = new URLSearchParams(searchParams as Record<string, string>);
        if (enableCondition && !enableCondition(urlSearchParams)) {
            continue;
        }

        if (type === 'dragBarFilter') {
            const min = parseInt(searchParams[`${key}_min`] as string, 10) || DEFAULT_MIN_DURATION;
            const max = parseInt(searchParams[`${key}_max`] as string, 10) || DEFAULT_MAX_DURATION;

            if (min !== DEFAULT_MIN_DURATION || max !== DEFAULT_MAX_DURATION) {
                const durationLabel = getDurationLabel(min, max);

                if (durationLabel) {
                    applied.push({
                        key,
                        value: `${min}-${max}`,
                        valueLabel: durationLabel,
                    });
                }
            }
            continue;
        }

        const raw = searchParams[key];
        if (raw === undefined) continue;
        let paramValues = raw;

        if (!Array.isArray(raw)) {
            paramValues = getArrayValueFromSearchParam(raw) as string[];
        }

        for (const paramValue of paramValues) {
            const option = filter.options.find((o) => o.urlId === paramValue);
            if (!option) continue;

            applied.push({
                key,
                urlId: option.urlId,
                value: option?.value || paramValue,
                valueLabel: option?.label || paramValue,
            });
        }
    }
    return applied;
}

export const removeAppliedFilter = (filter: AppliedFilter, currentParams: URLSearchParams) => {
    const searchParams = new URLSearchParams(currentParams.toString());

    if (filter.key === 'duration') {
        searchParams.delete('duration_min');
        searchParams.delete('duration_max');
        return searchParams;
    }

    const values = searchParams.getAll(filter.key);
    if (!values) return;

    for (const value of values) {
        // Remove value from single array value in string
        const newArrayValue = removeFilterArrayValueFromSearchParam(value, filter);
        if (newArrayValue) {
            // NOTE: delimiters will be url encoded if they still exist.
            searchParams.set(filter.key, newArrayValue);
            return searchParams;
        }

        if (value === filter?.urlId) {
            searchParams.delete(filter.key, filter.urlId);
            return searchParams;
        }

        if (value === filter.value) {
            searchParams.delete(filter.key, filter.value);
            return searchParams;
        }
    }

    return searchParams;
};

export const removeAllFilterQueryParams = (currentParams: URLSearchParams, filterKeys: string[]) => {
    const searchParams = new URLSearchParams(currentParams.toString());
    const searchParamsKeys = Array.from(searchParams.keys());
    const paramsToRemove = searchParamsKeys.filter((paramKey) =>
        // todo: Perform exact key validation i.e. duration_min/duration_max instead of partial matches 'duration'
        filterKeys.some((filterKey) => paramKey.includes(filterKey)),
    );
    paramsToRemove.forEach((key) => {
        if (typeof key === 'string') {
            searchParams.delete(key);
        }
    });
    return searchParams;
};

/**
 * Maps ContentClassEnum values to SearchContentClass keys used in filterConfig.
 * ContentClassEnum.Image is 'image' (singular) but filterConfig uses 'images' (plural).
 */
function mapContentClassToFilterKey(contentClass: ContentClassEnum): SearchContentClass {
    // ContentClassEnum values are: "audio", "video", "image"
    // filterConfig keys are: "audio", "video", "images"
    if (contentClass === ContentClassEnum.Image) {
        return 'images';
    }
    // TypeScript knows contentClass is now "audio" | "video", which match SearchContentClass
    return contentClass as SearchContentClass;
}

export const getCategoriesBySearchParamsApplied = (contentClass: ContentClassEnum, searchParams: URLSearchParams) => {
    const filterKey = mapContentClassToFilterKey(contentClass);
    return filterConfig[filterKey]
        .filter((obj: FilterConfig) => obj.key === SearchUrlParamKeys.CATEGORIES && obj.enableCondition?.(searchParams))
        .flatMap((obj: FilterConfig) => obj.options);
};

// NOTE: might replicate/abstract this logic for other mappings if
// the url param value and the search-gateway param value are different
// TODO: might move this logic into search-gateway-client rules
export const mapCategoryParamsToSearchGatewayParams = (
    categories?: string | string[],
    categoryOptions: FilterOption[] = [],
): Record<string, string[]> => {
    if (!categories) return {};

    const allCategoryNames = !Array.isArray(categories) ? getArrayValueFromSearchParam(categories) : categories;
    const categoryUrlIds = allCategoryNames.length ? allCategoryNames : [categories];
    const params: Record<string, string[]> = {};

    for (const categoryUrlId of categoryUrlIds) {
        const option = categoryOptions.find((opt) => opt.urlId === categoryUrlId);
        if (option) {
            const { apiKey = '' } = option;
            params[apiKey] = params[apiKey] ? [...params[apiKey], option.value] : [option.value];
        }
    }
    return params;
};
